import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  User, Briefcase, MapPin, Clock, GraduationCap, Code, Award,
  CheckCircle, XCircle, Phone, Pause, Loader2, AlertTriangle,
  ChevronLeft, ChevronRight, Star, Calendar, Building2, Zap,
  ThumbsUp, ThumbsDown, RotateCcw, ArrowLeft, Target, TrendingUp,
  FileText, Languages, Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HRSwipeReviewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [decisions, setDecisions] = useState([]);
  const [jobInfo, setJobInfo] = useState(null);
  const cardRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);

  useEffect(() => {
    loadCandidates();
  }, [token]);

  const loadCandidates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr-pipeline/swipe-review/${token}`);
      if (res.status === 410) {
        setError('expired');
        return;
      }
      if (!res.ok) throw new Error('Link not found');
      const result = await res.json();
      setCandidates(result.candidates || []);
      setJobInfo(result.job);
    } catch (err) {
      setError('not_found');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, reason = null) => {
    if (currentIndex >= candidates.length) return;
    
    const candidate = candidates[currentIndex];
    setActionLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('action', action);
      formData.append('candidate_id', candidate.id);
      if (reason) formData.append('reason', reason);

      const res = await fetch(`${API_URL}/api/hr-pipeline/swipe-action/${token}`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) throw new Error('Action failed');
      
      const result = await res.json();
      
      setDecisions([...decisions, { 
        candidate, 
        action, 
        interview_token: result.interview_token 
      }]);
      
      setSwipeDirection(action === 'shortlisted' ? 'right' : 'left');
      
      setTimeout(() => {
        setSwipeDirection(null);
        setCurrentIndex(currentIndex + 1);
      }, 300);
      
      if (action === 'shortlisted') {
        toast.success('Shortlisted! Schedule interview next.');
      } else if (action === 'rejected') {
        toast.info('Candidate rejected');
      }
      
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err) {
      toast.error('Failed to record action');
    } finally {
      setActionLoading(false);
    }
  };

  const undoLast = () => {
    if (decisions.length === 0) return;
    setDecisions(decisions.slice(0, -1));
    setCurrentIndex(currentIndex - 1);
    toast.info('Undo successful');
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    startX.current = e.touches ? e.touches[0].clientX : e.clientX;
    currentX.current = startX.current;
  };

  const handleTouchMove = (e) => {
    if (!cardRef.current) return;
    currentX.current = e.touches ? e.touches[0].clientX : e.clientX;
    const diff = currentX.current - startX.current;
    if (Math.abs(diff) > 10) {
      cardRef.current.style.transform = `translateX(${diff * 0.3}px) rotate(${diff * 0.02}deg)`;
    }
  };

  const handleTouchEnd = () => {
    if (!cardRef.current) return;
    const diff = currentX.current - startX.current;
    
    if (Math.abs(diff) > 120) {
      if (diff > 0) {
        // Swipe RIGHT = SHORTLIST (no popup)
        handleAction('shortlisted');
      } else {
        // Swipe LEFT = REJECT directly (no popup on swipe)
        handleAction('rejected');
      }
    }
    cardRef.current.style.transform = '';
    // Reset values
    startX.current = 0;
    currentX.current = 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-2 border-slate-900">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">
              {error === 'expired' ? 'Session Expired' : 'Session Not Found'}
            </h1>
            <p className="text-slate-600">Please contact the recruiter for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All candidates reviewed
  if (currentIndex >= candidates.length) {
    const shortlisted = decisions.filter(d => d.action === 'shortlisted');
    const rejected = decisions.filter(d => d.action === 'rejected');
    
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-2 border-slate-900">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Review Complete</h1>
            <p className="text-slate-600 mb-6">You've reviewed all candidates for this position.</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 border-2 border-green-600 rounded-lg p-4">
                <ThumbsUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-green-700">{shortlisted.length}</p>
                <p className="text-sm text-green-600 font-medium">Shortlisted</p>
              </div>
              <div className="bg-red-50 border-2 border-red-600 rounded-lg p-4">
                <ThumbsDown className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-red-700">{rejected.length}</p>
                <p className="text-sm text-red-600 font-medium">Rejected</p>
              </div>
            </div>
            
            {shortlisted.length > 0 && (
              <div className="space-y-2 text-left">
                <p className="font-semibold text-slate-800 mb-3">Schedule Interviews:</p>
                {shortlisted.map((d, i) => (
                  <Button 
                    key={i} 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => navigate(`/interview-schedule/${d.interview_token}`)}
                  >
                    <Calendar className="w-4 h-4 mr-2 text-green-600" />
                    {d.candidate.name}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const candidate = candidates[currentIndex];
  const progress = ((currentIndex + 1) / candidates.length) * 100;
  const matchedSkills = candidate.skill_matches?.filter(s => s.status === 'matched') || [];
  const missingSkills = candidate.skill_matches?.filter(s => s.status === 'missing') || [];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Professional Header */}
      <div className="bg-white border-b-2 border-slate-900">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Reviewing Candidates For</p>
              <h1 className="text-lg font-bold text-slate-900">{jobInfo?.title || 'Position'}</h1>
              {jobInfo?.company_name && (
                <p className="text-sm text-slate-600 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {jobInfo.company_name}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{currentIndex + 1}<span className="text-slate-400">/{candidates.length}</span></p>
              <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-slate-900 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card Container */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div 
          ref={cardRef}
          className={`bg-white border-2 border-slate-900 rounded-lg shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-transform duration-300 ${
            swipeDirection === 'left' ? '-translate-x-full opacity-0' :
            swipeDirection === 'right' ? 'translate-x-full opacity-0' : ''
          }`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Match Score Banner */}
          <div className="bg-slate-900 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                  <span className="text-2xl font-black text-slate-900">{Math.round(candidate.match_score || 0)}%</span>
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Match Score</p>
                  <p className="text-lg font-bold">
                    {candidate.match_score >= 80 ? 'Excellent Match' : 
                     candidate.match_score >= 70 ? 'Strong Match' :
                     candidate.match_score >= 60 ? 'Good Match' : 'Potential Match'}
                  </p>
                </div>
              </div>
              <Target className="w-10 h-10 text-white/30" />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="max-h-[55vh] overflow-y-auto">
            {/* Candidate Header */}
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-slate-900 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                  {candidate.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-slate-900">{candidate.name}</h2>
                  <p className="text-slate-600 font-medium">{candidate.current_designation}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-500">
                    {candidate.current_location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {candidate.current_location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {candidate.total_experience_years || 0} years exp.
                    </span>
                    {candidate.current_company && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {candidate.current_company}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Why Good Fit - Matching Keywords */}
            <div className="px-6 py-4 bg-green-50 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" />
                Why This Candidate Fits the JD
              </h3>
              <ul className="space-y-2">
                {(candidate.matching_points || []).map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Skills Match Breakdown */}
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Code className="w-5 h-5 text-slate-600" />
                Skills Match Breakdown
              </h3>
              
              {/* Matched Skills */}
              {matchedSkills.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wider text-green-600 font-semibold mb-2">
                    ✓ Matched Skills ({matchedSkills.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {matchedSkills.map((s, i) => (
                      <Badge key={i} className="bg-green-100 text-green-800 border border-green-300 hover:bg-green-100">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {s.skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Missing Skills */}
              {missingSkills.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-red-600 font-semibold mb-2">
                    ✗ Missing Skills ({missingSkills.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {missingSkills.map((s, i) => (
                      <Badge key={i} variant="outline" className="border-red-300 text-red-700">
                        <XCircle className="w-3 h-3 mr-1" />
                        {s.skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* All Technical Skills */}
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-slate-600" />
                All Technical Skills
              </h3>
              <div className="flex flex-wrap gap-2">
                {(candidate.technical_skills || candidate.skills || []).map((skill, i) => (
                  <Badge key={i} variant="outline" className="border-slate-300 text-slate-700">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Education */}
            {candidate.education?.length > 0 && (
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-slate-600" />
                  Education
                </h3>
                <div className="space-y-2">
                  {candidate.education.slice(0, 3).map((edu, i) => (
                    <div key={i} className="text-sm">
                      <p className="font-semibold text-slate-800">
                        {typeof edu === 'string' ? edu : (edu.degree || edu.qualification || 'Degree')}
                      </p>
                      {edu.institution && <p className="text-slate-600">{edu.institution}</p>}
                      {edu.year && <p className="text-slate-500 text-xs">{edu.year}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Work Experience */}
            {candidate.work_experience?.length > 0 && (
              <div className="px-6 py-4">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Work Experience
                </h3>
                <div className="space-y-4">
                  {candidate.work_experience.slice(0, 3).map((exp, i) => (
                    <div key={i} className="border-l-2 border-slate-300 pl-4">
                      <p className="font-semibold text-slate-800">{exp.title || exp.designation}</p>
                      <p className="text-slate-600 text-sm">{exp.company}</p>
                      {exp.duration && <p className="text-slate-500 text-xs">{exp.duration}</p>}
                      {exp.description && (
                        <p className="text-slate-600 text-sm mt-1">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons - Large & Prominent */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-900 p-4 shadow-lg pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-4 gap-3">
              {/* Undo Button */}
              <button
                onClick={undoLast}
                disabled={decisions.length === 0}
                className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-all ${
                  decisions.length === 0 
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-600 active:scale-95'
                }`}
              >
                <RotateCcw className="w-6 h-6 mb-1" />
                <span className="text-xs font-semibold">Undo</span>
              </button>

              {/* Reject Button */}
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
                className="flex flex-col items-center justify-center py-3 px-2 rounded-xl bg-red-500 hover:bg-red-600 text-white active:scale-95 transition-all shadow-md"
              >
                <ThumbsDown className="w-7 h-7 mb-1" />
                <span className="text-xs font-bold">REJECT</span>
              </button>

              {/* Shortlist Button */}
              <button
                onClick={() => handleAction('shortlisted')}
                disabled={actionLoading}
                className="flex flex-col items-center justify-center py-3 px-2 rounded-xl bg-green-500 hover:bg-green-600 text-white active:scale-95 transition-all shadow-md"
              >
                {actionLoading ? (
                  <Loader2 className="w-7 h-7 mb-1 animate-spin" />
                ) : (
                  <ThumbsUp className="w-7 h-7 mb-1" />
                )}
                <span className="text-xs font-bold">SHORTLIST</span>
              </button>

              {/* Hold Button */}
              <button
                onClick={() => handleAction('on_hold')}
                disabled={actionLoading}
                className="flex flex-col items-center justify-center py-3 px-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white active:scale-95 transition-all shadow-md"
              >
                <Pause className="w-6 h-6 mb-1" />
                <span className="text-xs font-bold">HOLD</span>
              </button>
            </div>

            {/* Swipe Instructions */}
            <p className="text-center text-slate-400 text-xs mt-3">
              ← Swipe left to reject • Swipe right to shortlist →
            </p>
          </div>
        </div>

        {/* Spacer for fixed bottom buttons */}
        <div className="h-32"></div>
      </div>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle>Reject {candidate?.name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Provide a reason for rejection (optional):</p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Skills gap in required areas, experience level mismatch..."
              rows={3}
              className="border-2 border-slate-300"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700" 
                onClick={() => handleAction('rejected', rejectReason)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Reject Candidate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
